import { AddCircleOutlineRounded } from "@mui/icons-material";
import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useBulkInsertActivityTypesMutation } from "../../../../../api/services/bulkInsertActivity/bulkInsertApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  BulkInsertFooter,
  FormController,
  InformationModal,
  MuiBox,
  MuiButtonTertiary,
  MuiGrid,
  MuiTextField,
  MuiTypography,
  WarningConfirmationModal,
} from "../../../../../components";
import { MAX_LENGTH_75 } from "../../../../../helpers/constants";
import { setApiDataToDropDownOption } from "../../../../../helpers/utils";
import useAccordion from "../../../../../hooks/useAccordion";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import {
  bulkActivityListSelector,
  bulkActivitySelectedParticipantsSelector,
} from "../../../../../redux/slices/program/programSelector";
import {
  setBulkActivityList,
  updateParticipantDeletion,
} from "../../../../../redux/slices/program/programSlice";
import { AccordionHeaderRight } from "../../../../../styles/common.style";
import { activityTimelineSiteCoreKeys } from "../../../../createProgram/siteCore/SiteCoreKeys";
import { CreateBulkActivityFormKey } from "../../../modelsAndSchemas/Models";
import { bulkActivitySiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { BulkActivityDetailsWrapper } from "./BulkActivityDetails.style";
import { ActivitiesResult } from "./activitiesResult/ActivitiesResult";
import { BulkActivityAddActivity } from "./bulkActivityAddActivity/BulkActivityAddActivity";
import { SelectedParticipantResult } from "./selectedParticipantResult/SelectedParticipantResult";

interface BulkActivityDetailsProps {
  pageId: string;
  handleBackButtonClick: () => void;
  handleNavigateBack: () => void;
  onConfirmBtnClick: () => void;
  currentStep?: number;
}

const initialAccordionStatus = {
  participants: false,
  all: false,
  activities: true,
};

export interface EditDeleteState {
  id: string | number;
  isEdit: boolean;
  isDelete: boolean;
  entityType: "activity" | "participant" | undefined;
}

export const BulkActivityDetails: FC<BulkActivityDetailsProps> = ({
  pageId,
  handleBackButtonClick,
  handleNavigateBack,
  onConfirmBtnClick,
  currentStep,
}) => {
  const { accordionStatus, handleIndividualAccordionChange } = useAccordion(
    initialAccordionStatus,
  );

  const [editDeleteState, setEditDeleteState] = useState<EditDeleteState>({
    id: "",
    isEdit: false,
    isDelete: false,
    entityType: undefined,
  });

  const [showInformationModal, setShowInformationModal] = useState(false);

  const activityList = useSelector(bulkActivityListSelector);

  const [isOpenActivity, setIsOpenActivity] = useState(false);

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  const dispatch = useDispatch();
  const { control } = useFormContext();

  const {
    commonLabels,
    fetchLabel,
    fetchComponentTitle,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const filteredParticipants = useSelector(
    bulkActivitySelectedParticipantsSelector,
  );

  const selectedParticipants = useMemo(
    () => filteredParticipants?.filter((participant) => participant.isSelected),
    [filteredParticipants],
  );

  const programIds = useMemo(() => {
    return selectedParticipants?.map((p) => p.programId) || [];
  }, [selectedParticipants]);

  useEffect(() => {
    if (currentStep === 1) {
      fetchActivityTypeList({
        participants: programIds,
      });
    }
  }, [currentStep, programIds]);

  const {
    data: activityTypeListData,
    isPending: isActivityTypePending,
    isError: isActivityTypeError,
    mutateAsync: fetchActivityTypeList,
  } = useBulkInsertActivityTypesMutation();

  const getLabel = (key: string) => {
    return fetchLabel(activityTimelineSiteCoreKeys.activityTimeline, key);
  };

  const getSelectedParticipantLabel = (key: string) => {
    return fetchLabel(bulkActivitySiteCoreKeys.selectedParticipants, key);
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isActivityTypePending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isActivityTypePending]);

  useEffect(() => {
    if (isActivityTypeError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isActivityTypeError]);

  const onCreateActivityClick = () => {
    setIsOpenActivity(true);
  };

  const handleActivityModalClose = () => {
    setIsOpenActivity(false);
    setEditDeleteState({
      id: "",
      isEdit: false,
      isDelete: false,
      entityType: undefined,
    });
  };

  const handleEditActivity = (id: number) => {
    setEditDeleteState({
      id: id,
      isEdit: true,
      isDelete: false,
      entityType: "activity",
    });
    setIsOpenActivity(true);
  };

  const handleDeleteActivity = (id: number) => {
    setEditDeleteState({
      id: id,
      isEdit: false,
      isDelete: true,
      entityType: "activity",
    });
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteClick = () => {
    if (editDeleteState.id) {
      if (editDeleteState.entityType === "activity") {
        const activities = activityList.filter(
          (activity) => activity.id !== editDeleteState.id,
        );
        dispatch(setBulkActivityList(activities));
        dispatch(
          toggleToast({
            open: true,
            message: getLabel(
              activityTimelineSiteCoreKeys.activityDeletedToastMessage,
            ),
          }),
        );
      } else if (editDeleteState.entityType === "participant") {
        dispatch(
          updateParticipantDeletion({ id: editDeleteState.id as string }),
        );
        dispatch(
          toggleToast({
            open: true,
            message: getSelectedParticipantLabel(
              bulkActivitySiteCoreKeys.participantDeletedToastMessage,
            ),
          }),
        );
      }
      handleModalClose();
    }
  };

  const handleModalClose = () => {
    if (showDeleteConfirmModal) {
      setShowDeleteConfirmModal(false);
      setEditDeleteState({
        id: "",
        isEdit: false,
        isDelete: false,
        entityType: undefined,
      });
    }
  };

  const handleDeleteParticipant = (id: string) => {
    if (selectedParticipants.length <= 2) {
      setShowInformationModal(true);
      setEditDeleteState({
        id: "",
        isEdit: false,
        isDelete: true,
        entityType: undefined,
      });
    } else {
      setEditDeleteState({
        id,
        isEdit: false,
        isDelete: true,
        entityType: "participant",
      });
      setShowDeleteConfirmModal(true);
    }
  };

  const renderHeaderTitle = (labelKey: string, selectedCount: number) => {
    return (
      <MuiBox className="title-container">
        <MuiTypography
          variant="h2SemiBold"
          className="form-header-left"
          component="h2"
        >
          {labelKey}
        </MuiTypography>
        {selectedCount > 0 && (
          <MuiTypography variant="h3Bold" className="selected-count">
            {selectedCount}
          </MuiTypography>
        )}
      </MuiBox>
    );
  };

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={fetchComponentTitle(labelKey)} />;
  };

  const renderParticipantActivityHeader = (isParticipantHeader: boolean) => {
    return (
      <MuiGrid container className="form-header">
        <AccordionHeaderRight className="with-custom-mobile-padding">
          {isParticipantHeader ? (
            <MuiButtonTertiary
              data-testid={`${pageId}-add-participants`}
              onClick={handleBackButtonClick}
            >
              <AddCircleOutlineRounded />
              {getSelectedParticipantLabel(
                bulkActivitySiteCoreKeys.addParticipantLink,
              )}
            </MuiButtonTertiary>
          ) : (
            <MuiButtonTertiary
              data-testid={`${pageId}-add-activity`}
              onClick={onCreateActivityClick}
            >
              <AddCircleOutlineRounded />
              {getLabel(activityTimelineSiteCoreKeys.addActivityLink)}
            </MuiButtonTertiary>
          )}
        </AccordionHeaderRight>
      </MuiGrid>
    );
  };

  const handleCloseInformation = () => {
    setShowInformationModal(false);
  };

  return (
    <BulkActivityDetailsWrapper data-testid={`${pageId}-bulk-activity-details`}>
      <AccordionHeader
        wrapperClassName="activity-details-accordion"
        title={renderHeaderTitle(
          fetchComponentTitle(bulkActivitySiteCoreKeys.selectedParticipants),
          selectedParticipants?.length ?? 0,
        )}
        onChange={() => handleIndividualAccordionChange("participants")}
        isExpanded={accordionStatus.participants}
      >
        {renderParticipantActivityHeader(true)}
        <SelectedParticipantResult
          rows={selectedParticipants ?? []}
          pageId={pageId}
          currentStep={1}
          handleDelete={handleDeleteParticipant}
        />
      </AccordionHeader>

      <MuiBox className="accordion-header" data-testid="bulk-activity-details">
        <AccordionHeader
          title={renderHeader(bulkActivitySiteCoreKeys.bulkProcessDetails)}
          isExpanded
        >
          <MuiBox className="name-field">
            <FormController
              key={CreateBulkActivityFormKey.bulkProcessName}
              control={control}
              name={CreateBulkActivityFormKey.bulkProcessName}
            >
              <MuiTextField
                id={CreateBulkActivityFormKey.bulkProcessName}
                placeholder={fetchLabel(
                  bulkActivitySiteCoreKeys.bulkProcessDetails,
                  bulkActivitySiteCoreKeys.bulkProcessNamePlaceholderText,
                )}
                maxLength={MAX_LENGTH_75}
                shouldTrimSpaces={false}
                isMandatory
                label={fetchLabel(
                  bulkActivitySiteCoreKeys.bulkProcessDetails,
                  bulkActivitySiteCoreKeys.bulkProcessName,
                )}
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={
                  commonValidationMessages.fieldSpecialCharactersTooltipMessage
                }
                tooltipHeaderTitle={
                  commonValidationMessages.allowedSpecialCharactersTitle
                }
              />
            </FormController>
          </MuiBox>
        </AccordionHeader>
      </MuiBox>

      <AccordionHeader
        wrapperClassName="activity-details-accordion"
        title={renderHeaderTitle(
          getLabel(activityTimelineSiteCoreKeys.bulkActivityListHeader),
          activityList?.length ?? 0,
        )}
        onChange={() => handleIndividualAccordionChange("activities")}
        isExpanded={accordionStatus.activities}
      >
        {renderParticipantActivityHeader(false)}
        <ActivitiesResult
          pageId={pageId}
          rows={activityList}
          handleEdit={handleEditActivity}
          handleDelete={handleDeleteActivity}
        />
      </AccordionHeader>

      {isOpenActivity && (
        <BulkActivityAddActivity
          show={isOpenActivity}
          handleCloseModal={handleActivityModalClose}
          pageId={pageId}
          filteredActivityTypes={setApiDataToDropDownOption(
            activityTypeListData,
          )}
          editActivityData={editDeleteState}
          programIds={programIds}
        />
      )}

      {showDeleteConfirmModal && (
        <WarningConfirmationModal
          show={showDeleteConfirmModal}
          onClose={handleModalClose}
          modalTitle={commonLabels.confirmation}
          onPrimaryButtonClick={handleDeleteClick}
          labelContent={
            editDeleteState.entityType === "activity"
              ? getLabel(
                  activityTimelineSiteCoreKeys.deleteActivityConfirmationText,
                )
              : getSelectedParticipantLabel(
                  bulkActivitySiteCoreKeys.deleteParticipantConfirmation,
                )
          }
          secondaryButtonTitle={commonLabels.no}
        />
      )}

      <BulkInsertFooter
        pageId={pageId}
        onConfirmClick={onConfirmBtnClick}
        handleNavigateBack={handleNavigateBack}
        isFiledUpdated={true}
        confirmButtonText={commonLabels.confirm}
        showBackButton={true}
        handleBackButtonClick={handleBackButtonClick}
      />

      {showInformationModal && (
        <InformationModal
          showModal={showInformationModal}
          handleModalClose={handleCloseInformation}
          handlePrimaryButtonClick={handleCloseInformation}
          title={commonLabels.warning}
          message={getSelectedParticipantLabel(
            bulkActivitySiteCoreKeys.minimumTwoParticipantsRequired,
          )}
        />
      )}
    </BulkActivityDetailsWrapper>
  );
};

export default BulkActivityDetails;
